import React, { useEffect, useMemo } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Stack,
  Typography,
} from '@mui/material';
import { CheckCircle as CheckCircleIcon } from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { clearCart } from '../../utils/chapterSuppliesCart';
import type { StorefrontCheckoutResponse } from '../../types/chapterSupplies';

const formatPrice = (v: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(
    v || 0
  );

const StorefrontConfirmation: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = (location.state || {}) as Partial<StorefrontCheckoutResponse>;

  const orderRef = useMemo(
    () => `CS-${Math.random().toString(36).slice(2, 8).toUpperCase()}`,
    []
  );

  useEffect(() => {
    clearCart();
  }, []);

  if (state.total == null || !state.order_id) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography sx={{ mb: 2 }}>
          No confirmation data found. Your order may have already been
          processed.
        </Typography>
        <Button
          variant="contained"
          onClick={() => navigate('/chapter-supplies/storefront')}
        >
          Return to Storefront
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Card>
        <CardContent>
          <Stack spacing={2} alignItems="flex-start">
            <Stack direction="row" spacing={1} alignItems="center">
              <CheckCircleIcon color="success" />
              <Typography variant="h4">Order Placed</Typography>
            </Stack>
            <Typography variant="body1">
              Thank you! Your chapter supplies order has been submitted.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Reference:{' '}
              {state.order_number != null
                ? `Order #${state.order_number}`
                : state.order_id || orderRef}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Status: {state.order_status_name || 'submitted'}
            </Typography>
            <Box sx={{ width: '100%', maxWidth: 420 }}>
              <Stack direction="row" justifyContent="space-between">
                <Typography>Subtotal</Typography>
                <Typography>{formatPrice(state.subtotal || 0)}</Typography>
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                <Typography>Freight</Typography>
                <Typography>
                  {formatPrice(state.shipping_amount || 0)}
                </Typography>
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                <Typography>Tax</Typography>
                <Typography>{formatPrice(state.tax || 0)}</Typography>
              </Stack>
              <Stack
                direction="row"
                justifyContent="space-between"
                sx={{ mt: 1 }}
              >
                <Typography variant="h6">Total</Typography>
                <Typography variant="h6" color="primary.main">
                  {formatPrice(state.total || 0)}
                </Typography>
              </Stack>
              {state.lines &&
                state.lines.some(
                  (l) => l.custom_text_lines && l.custom_text_lines.length > 0
                ) && (
                  <Box sx={{ mt: 2, width: '100%' }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Personalization
                    </Typography>
                    {state.lines.map((line, idx) =>
                      line.custom_text_lines &&
                      line.custom_text_lines.length > 0 ? (
                        <Typography
                          key={idx}
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 0.5 }}
                        >
                          <strong>
                            {line.item_display_name || line.item_name || 'Item'}
                            :
                          </strong>{' '}
                          {line.custom_text_lines.join(' · ')}
                          {line.personalization_display_name
                            ? ` (Member: ${line.personalization_display_name}${
                                line.personalization_first_name
                                  ? `; Name on item: ${line.personalization_first_name}`
                                  : ''
                              })`
                            : ''}
                        </Typography>
                      ) : null
                    )}
                  </Box>
                )}
            </Box>
            {state.approval_required && (
              <Alert severity="info" variant="filled">
                This order includes items requiring approval. Final payment
                processing will occur after approval.
              </Alert>
            )}
            <Button
              variant="contained"
              onClick={() => navigate('/chapter-supplies/storefront')}
            >
              Continue Shopping
            </Button>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};

export default StorefrontConfirmation;
